/**
 * @file Serialized optimistic Ordering item mutations.
 */

import {
    doc,
    getDocFromServer,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../../app/firebase/firebase.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
    createOrderingItem,
    deleteOrderingItem,
    saveOrderingItem,
    updateOrderingItemStatus,
} from "./orderingApi.js";
import {
    findOrderingItem,
    getOrderingState,
    placeOrderingItem,
    recordAuthoritativeOrderingItems,
    removeOrderingItem,
} from "./orderingState.js";

const pendingCreates = new Map();
const pendingEdits = new Map();
const pendingDeletes = new Map();
const statusQueues = new Map();
const desiredStatuses = new Map();
const statusQueueEpochs = new Map();
const CLOSED_STATUSES = new Set(["delivered", "cancelled"]);
let mutationSessionEpoch = 0;

function localTimestamp() {
    const now = Date.now();
    return {
        seconds: Math.floor(now / 1000),
        nanoseconds: (now % 1000) * 1e6,
    };
}

function showStatusFailure(error, onDismiss) {
    openConfirmationModal(
        "Status change failed",
        escapeHTML(error?.message || "The item status could not be changed."),
        async () => onDismiss?.(),
        false,
        "OK",
        null,
        "",
        "",
        {hideCancel: true},
    );
}

async function restoreAuthoritativeItem(itemId, generation, sessionEpoch) {
    try {
        const snapshot = await getDocFromServer(doc(db, "ordering_items", itemId));
        if (snapshot.exists() &&
            getOrderingState().routeGeneration === generation &&
            mutationSessionEpoch === sessionEpoch) {
            const authoritative = {id: snapshot.id, ...snapshot.data()};
            recordAuthoritativeOrderingItems([authoritative]);
            placeOrderingItem(authoritative);
            return authoritative;
        }
        if (!snapshot.exists() &&
            getOrderingState().routeGeneration === generation &&
            mutationSessionEpoch === sessionEpoch) {
            removeOrderingItem(itemId, {authoritative: true});
            return null;
        }
    } catch (error) {
        console.warn("Ordering status rollback refresh failed:", error);
    }
}

/**
 * Invalidates all route-owned status work. In-flight callables may finish, but
 * their completions can no longer mutate UI state or open overlays.
 * @returns {void}
 */
export function invalidateOrderingStatusMutations() {
    mutationSessionEpoch += 1;
    pendingCreates.clear();
    pendingEdits.clear();
    pendingDeletes.clear();
    desiredStatuses.clear();
    statusQueueEpochs.clear();
    statusQueues.clear();
}

/**
 * Places a new item on the board immediately and reconciles it to the
 * backend-generated item ID.
 * @param {object} payload Create payload plus actorId.
 * @param {Function} render Board render callback.
 * @returns {Promise<object>} Authoritative callable response.
 */
export function queueOrderingItemCreate(payload, render) {
    const generation = getOrderingState().routeGeneration;
    const sessionEpoch = mutationSessionEpoch;
    const tempId = `pending-create-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const timestamp = localTimestamp();
    const optimistic = {
        id: tempId,
        name: payload.name,
        due_date: payload.due_date ?? null,
        details: payload.details ?? null,
        listId: payload.listId,
        status: "open",
        isClosedResolved: false,
        latestNote: null,
        notesSearchText: "",
        createdBy: payload.actorId,
        createdAt: timestamp,
        updatedBy: payload.actorId,
        updatedAt: timestamp,
        __orderingPending: "create",
    };
    pendingCreates.set(tempId, optimistic);
    placeOrderingItem(optimistic);
    render();

    return (async () => {
        try {
            const request = {
                listId: payload.listId,
                name: payload.name,
                due_date: payload.due_date ?? null,
                details: payload.details ?? null,
            };
            const response = await createOrderingItem(request);
            if (mutationSessionEpoch !== sessionEpoch ||
                getOrderingState().routeGeneration !== generation) {
                return response;
            }
            pendingCreates.delete(tempId);
            removeOrderingItem(tempId, {authoritative: true});
            const created = {id: response.itemId, ...response.item};
            recordAuthoritativeOrderingItems([created]);
            placeOrderingItem(created);
            render();
            return response;
        } catch (error) {
            if (mutationSessionEpoch === sessionEpoch &&
                getOrderingState().routeGeneration === generation) {
                pendingCreates.delete(tempId);
                removeOrderingItem(tempId, {authoritative: true});
                render();
            }
            throw error;
        }
    })();
}

/**
 * Applies submitted item fields immediately and reconciles or rolls them back
 * against the backend-authoritative save.
 * @param {object} payload Save payload.
 * @param {object} current Cached authoritative item before the save.
 * @param {object} optimistic Submitted optimistic item.
 * @param {Function} render Board render callback.
 * @returns {Promise<object>} Authoritative callable response.
 */
export async function queueOrderingItemSave(payload, current, optimistic, render) {
    const generation = getOrderingState().routeGeneration;
    const sessionEpoch = mutationSessionEpoch;
    pendingEdits.set(payload.itemId, optimistic);
    placeOrderingItem({...optimistic, __orderingPending: "edit"});
    render();
    try {
        const response = await saveOrderingItem(payload);
        if (mutationSessionEpoch !== sessionEpoch ||
            getOrderingState().routeGeneration !== generation ||
            pendingDeletes.has(payload.itemId)) {
            return response;
        }
        pendingEdits.delete(payload.itemId);
        const saved = {id: payload.itemId, ...response.item};
        recordAuthoritativeOrderingItems([saved]);
        placeOrderingItem(saved);
        render();
        return response;
    } catch (error) {
        if (!pendingDeletes.has(payload.itemId) &&
            mutationSessionEpoch === sessionEpoch &&
            getOrderingState().routeGeneration === generation) {
            pendingEdits.delete(payload.itemId);
            recordAuthoritativeOrderingItems([current]);
            placeOrderingItem(current);
            render();
            await restoreAuthoritativeItem(payload.itemId, generation, sessionEpoch);
            render();
        }
        throw error;
    }
}

/**
 * Tombstones an item immediately, then reconciles the backend-authoritative
 * delete cascade or restores the latest authoritative snapshot on failure.
 * @param {string} itemId Ordering item id.
 * @param {Function} render Board render callback.
 * @param {object|null} [fallbackItem=null] Modal-cached item for missing-state retry.
 * @returns {Promise<object>} Authoritative callable response.
 */
export async function queueOrderingItemDelete(itemId, render, fallbackItem = null) {
    const current = findOrderingItem(itemId) || fallbackItem;
    const generation = getOrderingState().routeGeneration;
    const sessionEpoch = mutationSessionEpoch;
    pendingEdits.delete(itemId);
    pendingDeletes.set(itemId, current);
    desiredStatuses.delete(itemId);
    statusQueueEpochs.set(itemId, (statusQueueEpochs.get(itemId) || 0) + 1);
    statusQueues.delete(itemId);
    removeOrderingItem(itemId);
    render();
    try {
        const response = await deleteOrderingItem({itemId});
        if (mutationSessionEpoch !== sessionEpoch ||
            getOrderingState().routeGeneration !== generation) {
            return response;
        }
        pendingDeletes.delete(itemId);
        removeOrderingItem(itemId, {authoritative: true});
        render();
        return response;
    } catch (error) {
        if (mutationSessionEpoch === sessionEpoch &&
            getOrderingState().routeGeneration === generation) {
            pendingDeletes.delete(itemId);
            if (current) {placeOrderingItem(current);}
            render();
            await restoreAuthoritativeItem(itemId, generation, sessionEpoch);
            render();
        }
        throw error;
    }
}

/**
 * Applies a status transition immediately, then serializes backend writes.
 * @param {string} itemId Ordering item id.
 * @param {string} targetStatus Requested status.
 * @param {Function} render Board render callback.
 * @returns {Promise<void>} Serialized completion.
 */
export function queueOrderingStatusChange(itemId, targetStatus, render) {
    const current = findOrderingItem(itemId);
    if (!current) {return Promise.resolve();}
    const generation = getOrderingState().routeGeneration;
    const sessionEpoch = mutationSessionEpoch;
    placeOrderingItem({
        ...current,
        status: targetStatus,
        isClosedResolved: CLOSED_STATUSES.has(targetStatus),
        updatedAt: {
            seconds: Math.floor(Date.now() / 1000),
            nanoseconds: (Date.now() % 1000) * 1e6,
        },
    });
    render();
    desiredStatuses.set(itemId, targetStatus);
    const queueEpoch = statusQueueEpochs.get(itemId) || 0;

    const previous = statusQueues.get(itemId) || Promise.resolve();
    let rollbackSnapshot = null;
    const next = previous.then(async () => {
        if ((statusQueueEpochs.get(itemId) || 0) !== queueEpoch ||
            mutationSessionEpoch !== sessionEpoch ||
            getOrderingState().routeGeneration !== generation) {return;}
        const authoritative = getOrderingState().authoritativeItemsById.get(itemId) || current;
        rollbackSnapshot = {...authoritative};
        const response = await updateOrderingItemStatus({
            itemId,
            expectedStatus: authoritative.status,
            expectedUpdatedAt: authoritative.updatedAt,
            status: targetStatus,
        });
        if ((statusQueueEpochs.get(itemId) || 0) !== queueEpoch ||
            mutationSessionEpoch !== sessionEpoch ||
            getOrderingState().routeGeneration !== generation) {return;}
        const saved = {id: itemId, ...response.item};
        recordAuthoritativeOrderingItems([saved]);
        const desiredStatus = desiredStatuses.get(itemId);
        placeOrderingItem(desiredStatus && desiredStatus !== saved.status ? {
            ...saved,
            status: desiredStatus,
            isClosedResolved: CLOSED_STATUSES.has(desiredStatus),
        } : saved);
        render();
    }).catch(async (error) => {
        if ((statusQueueEpochs.get(itemId) || 0) !== queueEpoch ||
            mutationSessionEpoch !== sessionEpoch ||
            getOrderingState().routeGeneration !== generation) {return;}
        statusQueueEpochs.set(itemId, queueEpoch + 1);
        desiredStatuses.delete(itemId);
        const confirmedSnapshot = rollbackSnapshot ||
            getOrderingState().authoritativeItemsById.get(itemId) || current;
        recordAuthoritativeOrderingItems([confirmedSnapshot]);
        placeOrderingItem(confirmedSnapshot);
        render();
        await restoreAuthoritativeItem(itemId, generation, sessionEpoch);
        if (mutationSessionEpoch !== sessionEpoch ||
            getOrderingState().routeGeneration !== generation) {return;}
        render();
        showStatusFailure(error, render);
    }).finally(() => {
        if (statusQueues.get(itemId) === next) {
            statusQueues.delete(itemId);
            desiredStatuses.delete(itemId);
            statusQueueEpochs.delete(itemId);
        }
    });
    statusQueues.set(itemId, next);
    return next;
}

/**
 * Reapplies pending optimistic status choices after an authoritative listener
 * refresh so unrelated snapshots cannot visually roll a queued action back.
 * @returns {void}
 */
export function applyPendingOrderingMutationOverlays() {
    pendingCreates.forEach((item) => placeOrderingItem(item));
    pendingEdits.forEach((item) => {
        placeOrderingItem({...item, __orderingPending: "edit"});
    });
    desiredStatuses.forEach((status, itemId) => {
        const authoritative = findOrderingItem(itemId) ||
            getOrderingState().authoritativeItemsById.get(itemId);
        if (!authoritative || authoritative.status === status) {return;}
        placeOrderingItem({
            ...authoritative,
            status,
            isClosedResolved: CLOSED_STATUSES.has(status),
        });
    });
    pendingDeletes.forEach((_item, itemId) => removeOrderingItem(itemId));
}
